import React, { useEffect, useState } from 'react'
import HighlightsBookShelf from 'components/modular/home/highlight-book-shelf'
import EnglishLevel from 'components/modular/english-level'
import Suggestions from 'components/modular/home/suggestions'
import OlympicsShelf from 'components/modular/home/olympics-shelf'
import BookCollection from 'components/modular/home/book-collections'
import { Module } from 'api/data.types'
import { tokens } from '@arvoreeducacao/bonsai-ds'
import { useFeatureToggles } from 'utils/hooks/use-feature-toggles/useFeatureToggles'
import { useUserInfo } from 'utils/hooks/use-userInfo'
import type { UserState } from 'utils/hooks/use-userInfo'
import { IconSelect, IconSelectWrapper } from './Tabingles.style'
import type { IViewOptionsDegrees } from 'components/modular/segment-and-content-tab/SegmentAndContentTab.type'
import { viewOptionsDegrees } from 'components/modular/segment-and-content-tab/constants'
import { useTranslation } from 'i18n'
import type { LanguageTokens } from 'i18n'
import { SpecialEntities } from 'utils/user'
import { post_event } from 'utils/analytics'
import ContentTabHome from 'components/content-tab-home/ContentTabHome'
import { useHistory } from 'react-router-dom'
import { COMUNICAR_STORY_TREE_HOME_ROOT_PATH } from 'routes/paths'

const READING_MODULES = [Module.LER, Module.LIVROS]

const hasReadingModule = (user: UserState) =>
  READING_MODULES.some(module => user.reader?.contentFilters?.includes(module))

const InglesShelves: React.FC<{ user: UserState; degree?: string }> = ({
  user,
  degree
}) => {
  const isStudentView = Boolean(degree)
  const { featureToggles } = useFeatureToggles()
  const showOlympicsShelf =
    Boolean(featureToggles?.olympicsShelvesComunicar) || !hasReadingModule(user)

  return (
    <>
      <HighlightsBookShelf
        contentFilter={Module.INGLES}
        degree={degree}
        isStudentView={isStudentView}
      />
      {showOlympicsShelf && (
        <OlympicsShelf degree={degree} isStudentView={isStudentView} />
      )}
      <EnglishLevel />
      {user.canAccessCollections && (
        <BookCollection
          contentFilter={Module.INGLES}
          degree={degree}
          isStudentView={isStudentView}
        />
      )}
      <Suggestions
        contentFilter={Module.INGLES}
        degree={degree}
        isStudentView={isStudentView}
      />
    </>
  )
}

const TabIngles: React.FC = () => {
  const [studentView, setStudentView] = useState<IViewOptionsDegrees | null>(
    null
  )
  const [defaultValue, setDefaultValue] = useState<IViewOptionsDegrees>()

  useEffect(() => {
    setDefaultValue(degreeOptionsMap[0])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const { user } = useUserInfo()
  const { t } = useTranslation()
  const isNonSchool = SpecialEntities.isNonSchool(user)
  const history = useHistory()

  const disableSegmentFilter = isNonSchool || SpecialEntities.isSescRs(user)
  const canSeeSegmentFilter = user.teacher && !disableSegmentFilter
  const isStoryTreeComunicarPath =
    history.location.pathname === COMUNICAR_STORY_TREE_HOME_ROOT_PATH

  const degreeOptionsMap = viewOptionsDegrees.map(item => ({
    ...item,
    text: t(item.text as LanguageTokens)
  }))

  const onSelectSchoolSegment = (value: IViewOptionsDegrees) => {
    if (value.text === 'Todos') {
      setStudentView(null)
    } else {
      setStudentView(value)
    }

    post_event(`Comunicar:VerComoEstudante`, value.text)
    setDefaultValue(value)
  }

  const windowWidth = window.innerWidth
  const iconSize = windowWidth > tokens.breakpoints.desktop ? 30 : 20

  return (
    <ContentTabHome
      key={'comunicar'}
      id="tab-comunicar-container"
      backgroundColor={tokens.colors.orange200}
    >
      {canSeeSegmentFilter && (
        <IconSelectWrapper isStoryTreeComunicarPath={isStoryTreeComunicarPath}>
          <IconSelect
            items={degreeOptionsMap}
            icon="graduation-cap"
            iconColor={tokens.colors.dark5}
            iconSize={iconSize}
            secondTitle={t('Segmento escolar')}
            onChange={value => {
              onSelectSchoolSegment(value)
            }}
            defaultOption={defaultValue}
            className={'school-segment-selector'}
            modalTitle={t('Filtrar segmento escolar')}
            backgroundColor={tokens.colors.orange200}
            borderColor={tokens.colors.orange400}
          />
        </IconSelectWrapper>
      )}
      <div id="current-tab" className="pl-2">
        <InglesShelves user={user} degree={studentView?.id} />
      </div>
    </ContentTabHome>
  )
}

export default TabIngles
